import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "../compnay/ManageCompany.css";
import "./manageRiderPerformanceRules.css";

const VIOLATION_TYPES = [
  { value: "LateDelivery", label: "Late Delivery" },
  { value: "OnTimeDelivery", label: "On-Time Delivery" },
  { value: "MissedAssignedOrder", label: "Missed Assigned Order" },
  { value: "OrderRejected", label: "Order Rejected" },
  { value: "OrderNotPickedUp", label: "Order Not Picked Up" },
  { value: "OrderCancelledByRider", label: "Order Cancelled by Rider" },
  { value: "CustomerComplaint", label: "Customer Complaint" },
  { value: "Other", label: "Other" },
];

const GRACE_TYPES = new Set(["LateDelivery", "OnTimeDelivery"]);

const emptyForm = {
  rule_ID: null,
  ruleName: "",
  violationType: "LateDelivery",
  graceMinutes: 10,
  isActive: true,
};

const emptyStarSettings = {
  evaluationPeriodDays: 15,
  deductViolationThreshold: 5,
  awardOnTimeThreshold: 5,
  starDelta: 1,
  isDeductEnabled: true,
  isAwardEnabled: true,
};

const ManageRiderPerformanceRules = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [filter, setFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [starSettings, setStarSettings] = useState(emptyStarSettings);

  const fieldStyle = useMemo(
    () => ({
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      color: isDarkMode ? "#ffffff" : "#495057",
      height: "44px",
      width: "100%",
    }),
    [isDarkMode]
  );

  const addButtonStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
      boxShadow: `0 2px 8px ${themeColor}40`,
    }),
    [themeColor]
  );

  const fetchRules = useCallback(async () => {
    try {
      setLoading(true);
      const [rulesRes, settingsRes] = await Promise.all([
        axiosInstance.get(APIs.GET_RIDER_PERFORMANCE_RULES),
        axiosInstance.get(APIs.GET_RIDER_STAR_SETTINGS),
      ]);

      if (rulesRes.data.status) {
        setRules(Array.isArray(rulesRes.data.data) ? rulesRes.data.data : []);
      } else {
        showErrorToast(rulesRes.data.message || "Failed to load detectors");
      }

      if (settingsRes.data.status && settingsRes.data.data) {
        const s = settingsRes.data.data;
        setStarSettings({
          evaluationPeriodDays: s.evaluationPeriodDays ?? s.EvaluationPeriodDays ?? 15,
          deductViolationThreshold: s.deductViolationThreshold ?? s.DeductViolationThreshold ?? 5,
          awardOnTimeThreshold: s.awardOnTimeThreshold ?? s.AwardOnTimeThreshold ?? 5,
          starDelta: s.starDelta ?? s.StarDelta ?? 1,
          isDeductEnabled: s.isDeductEnabled ?? s.IsDeductEnabled ?? true,
          isAwardEnabled: s.isAwardEnabled ?? s.IsAwardEnabled ?? true,
        });
      }
    } catch {
      showErrorToast("Failed to load performance settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const counts = useMemo(() => {
    const active = rules.filter((r) => r.isActive ?? r.IsActive).length;
    return {
      total: rules.length,
      active,
      inactive: rules.length - active,
    };
  }, [rules]);

  const visibleRules = useMemo(() => {
    if (filter === "active") return rules.filter((r) => r.isActive ?? r.IsActive);
    if (filter === "inactive") return rules.filter((r) => !(r.isActive ?? r.IsActive));
    return rules;
  }, [rules, filter]);

  const openCreate = () => {
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (rule) => {
    setForm({
      rule_ID: rule.rule_ID ?? rule.Rule_ID ?? null,
      ruleName: rule.ruleName ?? rule.RuleName ?? "",
      violationType: rule.violationType ?? rule.ViolationType ?? "LateDelivery",
      graceMinutes: rule.graceMinutes ?? rule.GraceMinutes ?? 0,
      isActive: rule.isActive ?? rule.IsActive ?? true,
    });
    setModalOpen(true);
  };

  const handleSaveSettings = async () => {
    const evalDays = parseInt(starSettings.evaluationPeriodDays, 10);
    const deduct = parseInt(starSettings.deductViolationThreshold, 10);
    const award = parseInt(starSettings.awardOnTimeThreshold, 10);
    const delta = parseInt(starSettings.starDelta, 10);

    if (!Number.isInteger(evalDays) || evalDays < 1 || evalDays > 365) {
      showErrorToast("Evaluation period must be 1-365 days");
      return;
    }
    if (!Number.isInteger(deduct) || deduct < 1) {
      showErrorToast("Deduct threshold must be at least 1");
      return;
    }
    if (!Number.isInteger(award) || award < 1) {
      showErrorToast("Award threshold must be at least 1");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post(APIs.POST_UPSERT_RIDER_STAR_SETTINGS, {
        evaluationPeriodDays: evalDays,
        deductViolationThreshold: deduct,
        awardOnTimeThreshold: award,
        starDelta: Number.isInteger(delta) && delta >= 1 ? delta : 1,
        isDeductEnabled: !!starSettings.isDeductEnabled,
        isAwardEnabled: !!starSettings.isAwardEnabled,
      });
      showSuccessToast("Star settings saved");
      fetchRules();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save star settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.ruleName.trim()) {
      showErrorToast("Detector name is required");
      return;
    }
    const grace = parseInt(form.graceMinutes, 10);
    const needsGrace = GRACE_TYPES.has(form.violationType);

    try {
      setLoading(true);
      await axiosInstance.post(APIs.POST_UPSERT_RIDER_PERFORMANCE_RULE, {
        rule_ID: form.rule_ID,
        ruleName: form.ruleName.trim(),
        violationType: form.violationType,
        graceMinutes: needsGrace && Number.isInteger(grace) && grace >= 0 ? grace : 0,
        isActive: !!form.isActive,
      });
      showSuccessToast(form.rule_ID ? "Detector updated" : "Detector created");
      setModalOpen(false);
      fetchRules();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save detector");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (rule) => {
    const id = rule.rule_ID ?? rule.Rule_ID;
    const next = !(rule.isActive ?? rule.IsActive);
    try {
      setLoading(true);
      await axiosInstance.post(APIs.POST_SET_RIDER_PERFORMANCE_RULE_ACTIVE, {
        rule_ID: id,
        isActive: next,
      });
      showSuccessToast(next ? "Detector enabled" : "Detector disabled");
      fetchRules();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update detector status");
    } finally {
      setLoading(false);
    }
  };

  const labelFor = (list, value) => list.find((x) => x.value === value)?.label || value;

  const showGrace = (type) => GRACE_TYPES.has(type);

  return (
    <div className={`rpr-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}

      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Rider Performance Rules</li>
          </ol>
          <h1 className="page-header mb-0">Rider Performance Rules</h1>
          <p className="rpr-page-desc mb-0 mt-2">
            Detectors decide which events count. Shared settings below decide when stars go up or down (1–5).
          </p>
        </div>
        <button type="button" className="rpr-add-btn" style={addButtonStyle} onClick={openCreate}>
          <i className="fa fa-plus" aria-hidden="true" />
          Add detector
        </button>
      </div>

      <div
        className="rpr-info-banner"
        style={{
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="rpr-info-icon">
          <i className="fa fa-star" aria-hidden="true" />
        </div>
        <div className="flex-grow-1">
          <div className="rpr-info-title">Stars only — collective counters</div>
          <p className="rpr-info-text">
            Violations from all active detectors share one counter. Stars change only from the settings card.
            On-time deliveries use a separate award counter.
          </p>
          <div className="rpr-summary-pills mt-2">
            <span className="rpr-pill">{counts.total} detectors</span>
            <span className="rpr-pill">{counts.active} active</span>
            <span className="rpr-pill">{counts.inactive} inactive</span>
          </div>
        </div>
      </div>

      <div className="rpr-table-card rpr-settings-card mb-3">
        <div className="rpr-settings-header">
          <div>
            <div className="rpr-settings-title">Star adjustment settings</div>
            <p className="rpr-settings-desc mb-0">
              Deduct after N collective violations. Award after N on-time deliveries (On-Time detector must be active).
            </p>
          </div>
          <button
            type="button"
            className="rpr-add-btn"
            style={addButtonStyle}
            onClick={handleSaveSettings}
            disabled={loading}
          >
            Save settings
          </button>
        </div>
        <div className="rpr-settings-body">
          <div className="row g-3">
            <div className="col-md-3">
              <FormInput
                label="Evaluation period (days)"
                type="number"
                value={starSettings.evaluationPeriodDays}
                onchange={(e) =>
                  setStarSettings({ ...starSettings, evaluationPeriodDays: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <FormInput
                label="Violations to deduct 1 star"
                type="number"
                value={starSettings.deductViolationThreshold}
                onchange={(e) =>
                  setStarSettings({ ...starSettings, deductViolationThreshold: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <FormInput
                label="On-time to award 1 star"
                type="number"
                value={starSettings.awardOnTimeThreshold}
                onchange={(e) =>
                  setStarSettings({ ...starSettings, awardOnTimeThreshold: e.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <FormInput
                label="Star change amount"
                type="number"
                value={starSettings.starDelta}
                onchange={(e) => setStarSettings({ ...starSettings, starDelta: e.target.value })}
              />
            </div>
            <div className="col-md-6 d-flex align-items-center justify-content-between rpr-toggle-row">
              <div>
                <div className="fw-semibold">Deduct enabled</div>
                <div className="small text-muted mb-0">Collective violations can lower stars</div>
              </div>
              <ToggleSwitch
                isChecked={!!starSettings.isDeductEnabled}
                onToggle={(e) =>
                  setStarSettings({ ...starSettings, isDeductEnabled: e.target.checked })
                }
                name="isDeductEnabled"
              />
            </div>
            <div className="col-md-6 d-flex align-items-center justify-content-between rpr-toggle-row">
              <div>
                <div className="fw-semibold">Award enabled</div>
                <div className="small text-muted mb-0">On-time deliveries can raise stars</div>
              </div>
              <ToggleSwitch
                isChecked={!!starSettings.isAwardEnabled}
                onToggle={(e) =>
                  setStarSettings({ ...starSettings, isAwardEnabled: e.target.checked })
                }
                name="isAwardEnabled"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="rpr-table-card">
        <div className="rpr-table-toolbar">
          <div>
            <div className="rpr-settings-title mb-0">Violation detectors</div>
            <div className="rpr-meta">Enable types that feed the collective violation pool</div>
          </div>
          <div className="rpr-filter-group">
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "inactive", label: "Inactive" },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                className={`rpr-filter-btn ${filter === item.key ? "is-active" : ""}`}
                style={filter === item.key ? { background: themeColor } : undefined}
                onClick={() => setFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
            <button type="button" className="rpr-action-btn" onClick={fetchRules} disabled={loading}>
              <i className="fa fa-sync-alt me-1" aria-hidden="true" />
              Refresh
            </button>
          </div>
        </div>

        {visibleRules.length === 0 ? (
          <div className="rpr-empty">
            <div className="rpr-empty-icon">
              <i className="fa fa-list-alt" aria-hidden="true" />
            </div>
            <div className="rpr-empty-title">
              {rules.length === 0 ? "No detectors yet" : "No detectors in this filter"}
            </div>
            <p className="rpr-empty-text">
              {rules.length === 0
                ? "Add detectors for late delivery, rejects, and related events. Stars still use the settings card above."
                : "Try another filter, or add a new detector."}
            </p>
            {rules.length === 0 && (
              <button type="button" className="rpr-add-btn" style={addButtonStyle} onClick={openCreate}>
                <i className="fa fa-plus" aria-hidden="true" />
                Add first detector
              </button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Detector</th>
                  <th>Violation type</th>
                  <th>Grace</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRules.map((rule) => {
                  const id = rule.rule_ID ?? rule.Rule_ID;
                  const active = rule.isActive ?? rule.IsActive;
                  const type = rule.violationType ?? rule.ViolationType;
                  const grace = rule.graceMinutes ?? rule.GraceMinutes ?? 0;
                  return (
                    <tr key={id}>
                      <td>
                        <div className="rpr-rule-name">{rule.ruleName ?? rule.RuleName}</div>
                        <div className="rpr-meta">
                          {type === "OnTimeDelivery"
                            ? "Feeds award counter"
                            : "Feeds collective violation pool"}
                        </div>
                      </td>
                      <td>{labelFor(VIOLATION_TYPES, type)}</td>
                      <td>{showGrace(type) ? `${grace} min` : "—"}</td>
                      <td>
                        <span className={`rpr-status-badge ${active ? "is-active" : "is-inactive"}`}>
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "currentColor",
                              display: "inline-block",
                            }}
                          />
                          {active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="rpr-row-actions">
                          <button type="button" className="rpr-action-btn" onClick={() => openEdit(rule)}>
                            Edit
                          </button>
                          <button type="button" className="rpr-action-btn" onClick={() => toggleActive(rule)}>
                            {active ? "Disable" : "Enable"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CustomModal
        title={form.rule_ID ? "Edit detector" : "Add detector"}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSave}
        isCentered={true}
        submitButtonText="Save detector"
        modalSize="modal-md"
      >
        <div className="row g-3">
          <div className="col-12">
            <FormInput
              label="Detector name"
              value={form.ruleName}
              onchange={(e) => setForm({ ...form, ruleName: e.target.value })}
            />
          </div>
          <div className="col-12">
            <label className="form-label">Violation type</label>
            <select
              className="rpr-select"
              style={fieldStyle}
              value={form.violationType}
              onChange={(e) => setForm({ ...form, violationType: e.target.value })}
            >
              {VIOLATION_TYPES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {showGrace(form.violationType) && (
            <div className="col-12">
              <FormInput
                label="Grace time (minutes)"
                type="number"
                value={form.graceMinutes}
                onchange={(e) => setForm({ ...form, graceMinutes: e.target.value })}
              />
              <div className="small text-muted mt-1">
                Delivery is late only if assign-to-deliver exceeds this buffer.
              </div>
            </div>
          )}
          <div className="col-12 d-flex align-items-center justify-content-between">
            <div>
              <div className="fw-semibold">Status</div>
              <div className="small text-muted mb-0">
                Inactive detectors are not recorded and do not feed star counters.
              </div>
            </div>
            <ToggleSwitch
              isChecked={form.isActive}
              onToggle={(e) => setForm({ ...form, isActive: e.target.checked })}
              name="ruleIsActive"
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default ManageRiderPerformanceRules;
